import { getApiUrl, getAuthHeaders } from '../config/api';
import { API_CONFIG } from '../config/api';

export enum WithdrawalStatus {
  PENDING = 'pending',
  APPROVED = 'approved',
  REJECTED = 'rejected',
  PAID = 'paid',
}

export enum PayoutMethod {
  UPI = 'upi',
  BANK = 'bank',
}

export interface Doctor {
  id: number;
  fullName: string;
  email: string;
  phoneNumber: string;
  profileImage?: string;
}

export interface Withdrawal {
  id: number;
  doctorId: number;
  amount: number;
  fee: number;
  netAmount: number;
  currency: string;
  payoutMethod: PayoutMethod;
  upiId?: string;
  accountHolderName?: string;
  accountNumber?: string;
  ifscCode?: string;
  status: WithdrawalStatus;
  referenceId?: string;
  remarks?: string;
  processedAt?: string;
  createdAt: string;
  updatedAt: string;
  doctor?: Doctor;
}

export interface Wallet {
  id: number;
  doctorId: number;
  balance: number;
  totalEarned: number;
  totalWithdrawn: number;
  holdBalance: number;
  currency: string;
  isActive: boolean;
}

export interface WithdrawalResponse {
  success: boolean;
  data: Withdrawal;
  message?: string;
}

export interface WithdrawalDetailResponse {
  success: boolean;
  data: {
    withdrawal: Withdrawal;
    wallet?: Wallet;
  };
  message?: string;
}

export interface WithdrawalsListResponse {
  success: boolean;
  data: {
    withdrawals: Withdrawal[];
    pagination: {
      currentPage: number;
      totalPages: number;
      totalItems: number;
      itemsPerPage: number;
      hasNextPage: boolean;
      hasPrevPage: boolean;
    };
  };
  message?: string;
}

export interface WithdrawalFilters {
  page?: number;
  limit?: number;
  search?: string;
  status?: WithdrawalStatus | 'all';
  doctorId?: number;
  sortBy?: 'createdAt' | 'amount' | 'status';
  sortOrder?: 'asc' | 'desc';
}

export interface ApproveWithdrawalData {
  referenceId?: string;
}

export interface RejectWithdrawalData {
  remarks?: string;
}

class WithdrawalService {
  // Get all withdrawals with pagination and filtering
  async getWithdrawals(filters: WithdrawalFilters = {}): Promise<WithdrawalsListResponse> {
    const params = new URLSearchParams();
    
    if (filters.page) params.append('page', filters.page.toString());
    if (filters.limit) params.append('limit', filters.limit.toString());
    if (filters.search) params.append('search', filters.search);
    if (filters.status && filters.status !== 'all') params.append('status', filters.status);
    if (filters.doctorId) params.append('doctorId', filters.doctorId.toString());
    if (filters.sortBy) params.append('sortBy', filters.sortBy);
    if (filters.sortOrder) params.append('sortOrder', filters.sortOrder);

    const url = `${getApiUrl(API_CONFIG.ENDPOINTS.ADMIN_WITHDRAWALS)}?${params.toString()}`;
    
    const response = await fetch(url, {
      method: 'GET',
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Failed to fetch withdrawals');
    }

    return response.json();
  }

  // Get single withdrawal by ID
  async getWithdrawal(id: number): Promise<WithdrawalDetailResponse> {
    const response = await fetch(getApiUrl(API_CONFIG.ENDPOINTS.ADMIN_WITHDRAWAL_BY_ID(id)), {
      method: 'GET',
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Failed to fetch withdrawal');
    }

    return response.json();
  }

  // Approve withdrawal
  async approveWithdrawal(id: number, data: ApproveWithdrawalData = {}): Promise<WithdrawalResponse> {
    const response = await fetch(getApiUrl(API_CONFIG.ENDPOINTS.ADMIN_WITHDRAWAL_APPROVE(id)), {
      method: 'PATCH',
      headers: {
        ...getAuthHeaders(),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Failed to approve withdrawal');
    }

    return response.json();
  }

  // Reject withdrawal
  async rejectWithdrawal(id: number, data: RejectWithdrawalData = {}): Promise<WithdrawalResponse> {
    const response = await fetch(getApiUrl(API_CONFIG.ENDPOINTS.ADMIN_WITHDRAWAL_REJECT(id)), {
      method: 'PATCH',
      headers: {
        ...getAuthHeaders(),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Failed to reject withdrawal');
    }

    return response.json();
  }
}

export const withdrawalService = new WithdrawalService();

